import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { sortArticles } from "../lib/articleOrder";
import type { Article } from "./useArticles";

export function useFeaturedArticles(options?: { excludeIds?: number[] }) {
	const excludeIds = options?.excludeIds || [];
	const [articles, setArticles] = useState<Article[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchArticles = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const articlesQuery = () => supabase.from("articles").select("*");

			// Las fijadas deben entrar aunque sean antiguas: se piden primero por `position`.
			// Se piden de más porque una noticia movida (sort_at) puede no ser de las más recientes.
			let { data, error: supaError } = await articlesQuery()
				.order("position", { ascending: true, nullsFirst: false })
				.order("created_at", { ascending: false })
				.limit(40);
			// Si la columna `position` aún no existe, usar el orden por fecha
			if (supaError?.code === "42703") {
				({ data, error: supaError } = await articlesQuery()
					.order("created_at", { ascending: false })
					.limit(40));
			}

			if (supaError) throw supaError;

			let normalized = (data || []).map((a) => ({
				...a,
				tags: Array.isArray(a.tags) ? a.tags : [],
				content: a.content || "",
			}));

			// Última subida primero, luego las fijadas y el resto por fecha visible
			normalized = sortArticles(normalized);

			// Excluir IDs que ya aparecen en otras secciones (ej. Últimas Noticias)
			if (excludeIds.length > 0) {
				normalized = normalized.filter((a) => !excludeIds.includes(a.id));
			}

			// Máximo 8 noticias destacadas
			setArticles(normalized.slice(0, 8));
		} catch (err) {
			setError(err instanceof Error ? err.message : "Error al cargar artículos destacados");
		} finally {
			setLoading(false);
		}
	}, [excludeIds]);

	useEffect(() => {
		fetchArticles();

		const channel = supabase
			.channel("featured-articles-changes")
			.on(
				"postgres_changes",
				{ event: "*", schema: "public", table: "articles" },
				(payload) => {
					if (payload.eventType === "INSERT") {
						const newArticle = payload.new as Article;
						if (!newArticle?.created_at) {
							fetchArticles();
							return;
						}
						// Insertar optimistamente sin esperar refetch
						setArticles((prev) => {
							if (prev.some((a) => a.id === newArticle.id) || excludeIds.includes(newArticle.id)) return prev;
							const updated = [
								{
									...newArticle,
									tags: Array.isArray(newArticle.tags) ? newArticle.tags : [],
									content: newArticle.content || "",
								},
								...prev,
							];
							const sorted = sortArticles(updated);
							return sorted.slice(0, 8);
						});
					} else {
						// UPDATE o DELETE — hacer refetch completo
						fetchArticles();
					}
				}
			)
			.subscribe((status) => {
				if (status === "SUBSCRIBED") {
					console.log("[Realtime] Featured articles subscription active");
				}
			});

		return () => {
			supabase.removeChannel(channel);
		};
	}, [fetchArticles]);

	return { articles, loading, error, refetch: fetchArticles };
}